import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, Tabs, Tab, FooterButton } from 'Core/components';
import ShipmentAddEditForm from './shipment-add-edit-form';
import PartsTable from './parts-table';
import PickTicketTable from './pick-ticket-table';
import UserLogTable from './log-table';
import FileTable from './file-table';
import CustomPropTypes from 'Core/prop-types';
import { addShipment, doOperationOnShipment, editShipment } from './store/slice';
import { AppModal } from '../components/app-modals';
import { showToast } from 'Core/redux/toast/slice';
import { all_routes } from '../../router/all_routes';
import { SHIPMENT_OPERATIONS } from '../../core/constants/constants';
import { ShipmentService } from '../../service/api/shipment-service';
import { useLocation } from 'react-router-dom';
import { parse } from 'date-fns';

const ShipmentAddEdit = ({ showModal, modalData, onCloseModal }) => {
  console.log('ShipmentAddEdit component rendered');

  const shipmentData = modalData;
  const [formData, setFormData] = useState(shipmentData || null);
  const [partsData, setPartsData] = useState(shipmentData?.parts || []);
  const [pickTicketsData, setPickTicketsData] = useState(shipmentData?.shipmentPickTickets || []);
  const [filesData, setFilesData] = useState(shipmentData?.shippingDocuments || []);

  const dispatch = useDispatch();
  const location = useLocation();
  const isCustomerDashboard = location.pathname === all_routes.shipments;
  const isEditMode = Boolean(shipmentData?.id);

  const onChangesShipmentForm = data => {
    setFormData(data);
  };

  const onChangesParts = data => {
    setPartsData(data);
  };

  const onChangesFiles = data => {
    setFilesData(data);
  };

  const onChangesPickTickets = data => {
    setPickTicketsData(data);
  };

  const handleClose = () => {
    onCloseModal();
  };

  const body = () => {
    return (
      <div className="row">
        <Tabs>
          <Tab title="Shipment">
            <ShipmentAddEditForm data={formData} onChanges={onChangesShipmentForm} readOnlyAsp={isEditMode} />
          </Tab>
          <Tab title="Parts">
            <PartsTable data={partsData} onChanges={onChangesParts} />
          </Tab>
          <Tab title="Documents">
            <FileTable data={filesData} onChanges={onChangesFiles} />
          </Tab>
          <Tab title="Pick Ticket">
            <PickTicketTable data={pickTicketsData} onChanges={onChangesPickTickets} />
          </Tab>
          <Tab title="User Log">
            <UserLogTable
              asp={formData?.asp || shipmentData?.asp || ''}
              data={((shipmentData && shipmentData.shipmentLogs) || []).map(log => ({
              ...log,
              dateTime: log.actionDate,
              parsedDate: log.actionDate
                ? parse(log.actionDate, 'dd-MM-yyyy HH:mm:ss', new Date())
                : null
            }))} />
          </Tab>
        </Tabs>
      </div>
    );
  };

  const valid = () => {
    if (formData?.customerId === '' || formData?.status === '' || partsData?.length === 0) {
      dispatch(
        showToast({
          message: 'Please fill all required fields',
          type: 'danger',
        }),
      );
      return false;
    }
    return true;
  };

  const footer = () => {
    const onSave = () => {
      if (valid()) {
        const payload = {
          ...formData,
          parts: partsData,
          shippingDocuments: filesData.map(({ id, ...file }) => ({
            ...file,
          })),
          savedAt: new Date().toISOString(),
        };

        if (isEditMode) {
          dispatch(editShipment(payload));
        } else {
          dispatch(addShipment(payload));
        }
      }
    };

    const onOperation = (operationName) => {
      // Only use new implementation in customer dashboard (/shipments)
      if (isCustomerDashboard) {
        // Special handling for packing slip - use new API
        if (operationName === SHIPMENT_OPERATIONS.PACKING_SLIP) {
          if (formData?.id && formData?.asp) {
            try {
              ShipmentService.downloadPackingSlip(formData.id, formData.asp)
                .then(() => {
                  dispatch(
                    showToast({
                      message: 'Packing slip downloaded successfully',
                      type: 'success',
                    }),
                  );
                })
                .catch((error) => {
                  dispatch(
                    showToast({
                      message: error.message || 'Failed to download packing slip',
                      type: 'danger',
                    }),
                  );
                });
            } catch (error) {
              dispatch(
                showToast({
                  message: error.message || 'Failed to download packing slip',
                  type: 'danger',
                }),
              );
            }
          } else {
            dispatch(
              showToast({
                message: 'Shipment ID and ASP number are required for downloading packing slip',
                type: 'danger',
              }),
            );
          }
          return;
        }
        
        // Special handling for pick ticket - use new API
        if (operationName === SHIPMENT_OPERATIONS.PICK_TICKET) {
          if (formData?.id && formData?.asp) {
            try {
              ShipmentService.downloadPickTicket(formData.id, formData.asp)
                .then(() => {
                  dispatch(
                    showToast({
                      message: 'Pick ticket downloaded successfully',
                      type: 'success',
                    }),
                  );
                })
                .catch((error) => {
                  dispatch(
                    showToast({
                      message: error.message || 'Failed to download pick ticket',
                      type: 'danger',
                    }),
                  );
                });
            } catch (error) {
              dispatch(
                showToast({
                  message: error.message || 'Failed to download pick ticket',
                  type: 'danger',
                }),
              );
            }
          } else {
            dispatch(
              showToast({
                message: 'Shipment ID and ASP number are required for downloading pick ticket',
                type: 'danger',
              }),
            );
          }
          return;
        }
      }
      
      // Original implementation for /shipment-tile and other operations
      const isMandatoryOperation = [SHIPMENT_OPERATIONS.BOL, SHIPMENT_OPERATIONS.MANIFEST].includes(operationName);
      
      if (isCustomerDashboard) {
        // For customer dashboard, no trailer requirement for any operation
        const payload = {
          requestId: 0,
          filePath: "",
          printerName: "",
          requestedUser: localStorage.getItem('UserId'),
          status: "",
          param1: formData.id+"",
          param2: formData.asp,
          param3: formData.trailer || "",
          param4: "",
          param5: "",
          isAutoPrint: true,
          category: operationName,
          requestFrom: 'Shipping',
        }
        dispatch(doOperationOnShipment(payload));
      } else {
        // For shipment-tile, keep original implementation with trailer check
        if(!isMandatoryOperation || (isMandatoryOperation && formData.trailer)){
          const payload = {
            requestId: 0,
            filePath: "",
            printerName: "",
            requestedUser: localStorage.getItem('UserId'),
            status: "",
            param1: formData.id+"",
            param2: formData.asp,
            param3: formData.trailer,
            param4: "",
            param5: "",
            isAutoPrint: true,
            category: operationName,
            requestFrom: 'Shipping',
          }
          dispatch(doOperationOnShipment(payload));
        }
        else {
          dispatch(
            showToast({
              message: 'Trailer field is mandatory for this operation',
              type: 'danger',
            }),
          );
        }
      }
    }

    return (
      <div className="row">
        <div className="col-lg-8">
          <button onClick={() => onOperation(SHIPMENT_OPERATIONS.PICK_TICKET)} type="button" className="btn btn-info me-1">
            PickTicket
          </button>
          <button onClick={() => onOperation(SHIPMENT_OPERATIONS.LABEL)} type="button" className="btn btn-secondary me-1">
            Label
          </button>
          <button onClick={() => onOperation(SHIPMENT_OPERATIONS.BOL)} type="button" className="btn btn-info me-1">
            BOL
          </button>
          <button onClick={() => onOperation(SHIPMENT_OPERATIONS.MANIFEST)} type="button" className="btn btn-secondary me-1">
            Manifest
          </button>
          <button onClick={() => onOperation(SHIPMENT_OPERATIONS.PACKING_SLIP)} type="button" className="btn btn-info me-1">
            Packing Slip
          </button>
        </div>
        <div className="col-lg-4">
          <FooterButton
            cancelText="Cancel"
            hideSave={window.location.pathname == all_routes.shipments}
            saveText="Save Shipment"
            onSave={onSave}
            onCancel={handleClose}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      id="shipment-modal"
      show={showModal}
      size="xl"
      dialogClassName="modal-90w"
      title={isEditMode ? 'Edit Shipment' : 'Add Shipment'}
      body={body}
      footer={footer}
      handleClose={handleClose}
    />
  );
};

ShipmentAddEdit.propTypes = {
  showModal: PropTypes.bool,
  modalData: PropTypes.shape(CustomPropTypes.Shipment),
  onCloseModal: PropTypes.func,
};

export default AppModal(ShipmentAddEdit);
